import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, Clock, FileText, Home, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

type QuoteConfirmationState = {
  status?: "sent";
  name?: string;
  serviceType?: string;
  referenceId?: string;
  email?: string;
};

const serviceLabels: Record<string, string> = {
  manpower: "Manpower Supply",
  material: "Material Supply",
  equipment: "Equipment Rental",
  vehicle: "Vehicle Rental",
  maintenance: "Maintenance Services",
  consulting: "Consulting Services",
  other: "Other Services",
};

const QuoteConfirmation = () => {
  const location = useLocation();
  const state = (location.state ?? {}) as QuoteConfirmationState;
  const serviceLabel = state.serviceType ? serviceLabels[state.serviceType] ?? state.serviceType : "Quote Request";

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-28 pb-20">
        <section className="container mx-auto px-4">
          <Card className="mx-auto max-w-3xl border-primary/20 bg-card shadow-elegant">
            <CardContent className="p-8 md:p-12 text-center">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 text-primary">
                <CheckCircle2 className="h-11 w-11" aria-hidden="true" />
              </div>

              <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-secondary/40 bg-secondary/10 px-4 py-2 text-sm font-semibold text-foreground">
                <FileText className="h-4 w-4 text-primary" aria-hidden="true" />
                Quote submission status: Sent
              </p>

              <h1 className="mb-4 text-3xl font-bold text-accent md:text-5xl">
                Thank you{state.name ? `, ${state.name}` : ""}.
              </h1>
              <p className="mx-auto mb-8 max-w-2xl text-lg leading-relaxed text-muted-foreground">
                Your {serviceLabel} request has been submitted successfully. Our team will review the details and contact you within 24 hours.
              </p>

              {state.referenceId && (
                <div className="mx-auto mb-8 max-w-md rounded-md border border-primary/30 bg-primary/5 p-5">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Your Reference ID
                  </p>
                  <p className="text-2xl font-bold tracking-wider text-primary">
                    {state.referenceId}
                  </p>
                  {state.email && (
                    <p className="mt-2 text-sm text-muted-foreground">
                      A confirmation has been sent to{" "}
                      <span className="font-semibold text-foreground">{state.email}</span>.
                      Please keep this reference ID for follow-up.
                    </p>
                  )}
                </div>
              )}

              <div className="mb-8 grid gap-4 text-left md:grid-cols-2">
                <div className="rounded-md border border-border bg-background p-5">
                  <Clock className="mb-3 h-6 w-6 text-primary" aria-hidden="true" />
                  <h2 className="mb-2 text-lg font-semibold text-foreground">Current status</h2>
                  <p className="text-sm text-muted-foreground">Received and awaiting team review.</p>
                </div>
                <div className="rounded-md border border-border bg-background p-5">
                  <Mail className="mb-3 h-6 w-6 text-primary" aria-hidden="true" />
                  <h2 className="mb-2 text-lg font-semibold text-foreground">Need urgent support?</h2>
                  <p className="text-sm text-muted-foreground">Email sales@arabianalwaha.com directly.</p>
                </div>
              </div>

              <Button asChild className="min-h-11 px-6">
                <Link to="/">
                  <Home className="mr-2 h-4 w-4" aria-hidden="true" />
                  Back to homepage
                </Link>
              </Button>
            </CardContent>
          </Card>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default QuoteConfirmation;